import Dexie, { type EntityTable } from "dexie";
import type {
  AppSettings,
  AppUser,
  AuditLog,
  AuthSession,
  ImportBatch,
  InventoryBalance,
  InventoryMovement,
  MarketplaceListing,
  MarketplaceVariantListing,
  PendingSync,
  Product,
  ProductImportBatch,
  ProductVariant,
  VariantAlias,
} from "../types";
import { randomId } from "../utils/id";

export interface KeyValue {
  key: string;
  value: unknown;
}

export class WGDatabase extends Dexie {
  products!: EntityTable<Product, "id">;
  variants!: EntityTable<ProductVariant, "id">;
  balances!: EntityTable<InventoryBalance, "id">;
  movements!: EntityTable<InventoryMovement, "id">;
  pendingSync!: EntityTable<PendingSync, "id">;
  imports!: EntityTable<ImportBatch, "id">;
  auditLogs!: EntityTable<AuditLog, "id">;
  settings!: EntityTable<AppSettings, "id">;
  users!: EntityTable<AppUser, "id">;
  sessions!: EntityTable<AuthSession, "id">;
  kv!: EntityTable<KeyValue, "key">;
  variantAliases!: EntityTable<VariantAlias, "id">;
  marketplaceListings!: EntityTable<MarketplaceListing, "id">;
  marketplaceVariants!: EntityTable<MarketplaceVariantListing, "id">;
  productImports!: EntityTable<ProductImportBatch, "id">;

  constructor() {
    super("wg_tricot_estoque");
    this.version(1).stores({
      products: "id, &sku, name, category, status, updatedAt",
      variants: "id, productId, colorName, status, malhariaEnabled, updatedAt",
      balances: "id, &[variantId+warehouse], variantId, warehouse, updatedAt",
      movements: "id, operationId, variantId, warehouse, type, createdAt, syncStatus",
      pendingSync: "id, operationType, status, createdAt, updatedAt",
      imports: "id, &fileHash, source, status, createdAt",
      auditLogs: "id, entityType, entityId, userId, createdAt",
      settings: "id",
      users: "id, &email, role, status",
      sessions: "id",
      kv: "key",
    });
    this.version(2).stores({
      products: "id, &sku, name, category, status, updatedAt",
      variants: "id, productId, colorName, status, malhariaEnabled, updatedAt",
      balances: "id, &[variantId+warehouse], variantId, warehouse, updatedAt",
      movements: "id, operationId, variantId, warehouse, type, createdAt, syncStatus",
      pendingSync: "id, operationType, status, createdAt, updatedAt",
      imports: "id, &fileHash, source, status, createdAt",
      auditLogs: "id, entityType, entityId, userId, createdAt",
      settings: "id",
      users: "id, &email, role, status",
      sessions: "id",
      kv: "key",
      variantAliases: "id, &[productId+normalizedAlias], productId, variantId, kind, normalizedAlias, updatedAt",
      marketplaceListings: "id, &[marketplace+externalListingId], productId, importId, marketplace, updatedAt",
      marketplaceVariants: "id, listingId, productId, variantId, externalVariantId, marketplaceSku, updatedAt",
      productImports: "id, &fileHash, source, status, createdAt",
    });
  }
}

export const db = new WGDatabase();

const now = () => new Date().toISOString();
const id = randomId;

export async function getDeviceId(): Promise<string> {
  const current = await db.kv.get("device_id");
  if (typeof current?.value === "string") return current.value;
  const deviceId = id("device");
  await db.kv.put({ key: "device_id", value: deviceId });
  return deviceId;
}

export async function initializeDatabase(): Promise<void> {
  const configuredApi = import.meta.env.VITE_API_URL?.replace(/\/$/, "") ?? "";
  if (!(await db.settings.get("app"))) {
    await db.settings.put({
      id: "app",
      companyName: "WG Tricot",
      allowNegativeStock: false,
      defaultMinimumStock: 10,
      theme: "sistema",
      apiUrl: configuredApi,
      demoData: true,
    });
  }

  await getDeviceId();
  if ((await db.products.count()) === 0 && !(await db.kv.get("demo_removed"))) {
    await seedDemoData();
  }
}

export async function seedDemoData(): Promise<void> {
  const timestamp = now();
  const admin: AppUser = {
    id: "demo-admin",
    name: "Maria — Administradora",
    email: "admin@wgtricot.local",
    role: "administrador",
    canImport: true,
    status: "ativo",
  };

  const products: Product[] = [
    { id: "prod-cm001", name: "Conjunto Tricot Mousse", sku: "CM001", category: "Conjuntos", status: "ativo", createdAt: timestamp, updatedAt: timestamp },
    { id: "prod-vt001", name: "Vestido Tricot", sku: "VT001", category: "Vestidos", status: "ativo", createdAt: timestamp, updatedAt: timestamp },
    { id: "prod-bc001", name: "Blusa Canelada", sku: "BC001", category: "Blusas", status: "ativo", createdAt: timestamp, updatedAt: timestamp },
  ];

  const variants: ProductVariant[] = [
    { id: "var-cm-preto", productId: "prod-cm001", colorName: "Preto", colorCode: "#191919", minimumStock: 10, malhariaEnabled: true, status: "ativo", updatedAt: timestamp },
    { id: "var-cm-areia", productId: "prod-cm001", colorName: "Areia", colorCode: "#d8c3a9", minimumStock: 12, malhariaEnabled: false, status: "ativo", updatedAt: timestamp },
    { id: "var-cm-off", productId: "prod-cm001", colorName: "Off White", colorCode: "#f4f0e8", minimumStock: 10, malhariaEnabled: true, status: "ativo", updatedAt: timestamp },
    { id: "var-vt-preto", productId: "prod-vt001", colorName: "Preto", colorCode: "#191919", minimumStock: 8, malhariaEnabled: true, status: "ativo", updatedAt: timestamp },
    { id: "var-vt-verde", productId: "prod-vt001", colorName: "Verde", colorCode: "#718b72", minimumStock: 8, malhariaEnabled: false, status: "ativo", updatedAt: timestamp },
    { id: "var-bc-azul", productId: "prod-bc001", colorName: "Azul", colorCode: "#8ca9c4", minimumStock: 15, malhariaEnabled: false, status: "ativo", updatedAt: timestamp },
  ];

  const quantities: Record<string, [number, number?]> = {
    "var-cm-preto": [20, 80],
    "var-cm-areia": [40],
    "var-cm-off": [7, 60],
    "var-vt-preto": [18, 100],
    "var-vt-verde": [0],
    "var-bc-azul": [13],
  };

  const balances: InventoryBalance[] = [];
  const movements: InventoryMovement[] = [];
  for (const variant of variants) {
    const [galpao, malharia] = quantities[variant.id];
    balances.push({ id: `${variant.id}:galpao`, variantId: variant.id, warehouse: "galpao", quantity: galpao, version: 1, updatedAt: timestamp });
    if (galpao !== 0) movements.push({
      id: id("mov"), operationId: id("demo"), variantId: variant.id, warehouse: "galpao", type: "estoque_inicial", quantity: galpao,
      before: 0, after: galpao, userId: admin.id, userName: admin.name, source: "demo", note: "Carga de demonstração", createdAt: timestamp, syncStatus: "sincronizado",
    });
    if (variant.malhariaEnabled) {
      balances.push({ id: `${variant.id}:malharia`, variantId: variant.id, warehouse: "malharia", quantity: malharia ?? 0, version: 1, updatedAt: timestamp });
    }
  }

  await db.transaction("rw", [db.products, db.variants, db.balances, db.movements, db.users, db.settings], async () => {
    await db.products.bulkPut(products);
    await db.variants.bulkPut(variants);
    await db.balances.bulkPut(balances);
    await db.movements.bulkPut(movements);
    await db.users.put(admin);
    await db.settings.update("app", { demoData: true });
  });
}

export async function clearDemoData(): Promise<void> {
  await db.transaction("rw", [db.products, db.variants, db.balances, db.movements, db.imports, db.pendingSync, db.auditLogs, db.kv, db.settings, db.variantAliases, db.marketplaceListings, db.marketplaceVariants, db.productImports], async () => {
    await Promise.all([
      db.products.clear(), db.variants.clear(), db.balances.clear(), db.movements.clear(),
      db.imports.clear(), db.pendingSync.clear(), db.auditLogs.clear(),
      db.variantAliases.clear(), db.marketplaceListings.clear(), db.marketplaceVariants.clear(), db.productImports.clear(),
    ]);
    await db.kv.put({ key: "demo_removed", value: true });
    await db.settings.update("app", { demoData: false });
  });
}

export async function restoreDemoData(): Promise<void> {
  await db.kv.delete("demo_removed");
  await seedDemoData();
}
